/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GContextTypes } from '../../types';
import { GlobalContext } from '../../contexts';
import {
  Grid,
  Box,
  Button,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import { useMutation } from '@apollo/client';
import { getPresignedURL } from '../../graphql/mutation';
import { dublicateAlert, errorAlert, successAlert } from '../common/funcs';
import PopupLayout from '../shared/PopupLayout';
import TextFieldLocal from '../shared/TextFieldLocal';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { burl } from '../../constants';

const PopupArchive = ({
  open,
  onClose,
  row,
  isNew,
  addAction,
  editAction,
  theme,
  words,
  company,
}: any) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileInfo, setFileInfo] = useState<any>(null);
  const [alrt, setAlrt] = useState({ show: false, msg: '', type: undefined });

  const { register, handleSubmit, errors, reset, setValue } = useForm();
  const {
    translate: { isRTL },
  }: GContextTypes = useContext(GlobalContext);

  const [getURL] = useMutation(getPresignedURL);

  useEffect(() => {
    if (!isNew && row?._id) {
      setValue('title', row.title);
      setValue('titleAr', row.titleAr);
      setValue('desc', row.desc);
      if (row.filename) {
        setFileInfo({
          filename: row.filename,
          filetype: row.filetype,
          url: row.url,
          size: row.size,
        });
      }
    }
  }, [row]);

  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      await errorAlert(setAlrt, isRTL);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const timestamp = Date.now();
      const path = `${burl}/${company?.basename}/archive/${timestamp}_${file.name}`;

      const res = await getURL({
        variables: { filename: path, filetype: file.type },
      });
      const signedUrl = res.data?.getPresignedURL?.data;

      if (!signedUrl) {
        throw new Error('Failed to get upload URL');
      }

      const uploadRes = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-amz-acl': 'public-read',
        },
      });

      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }

      const publicUrl = signedUrl.split('?')[0];

      setFileInfo({
        filename: file.name,
        filetype: file.type,
        onlinename: path,
        url: publicUrl,
        size: file.size,
      });
      setUploadProgress(100);
    } catch (err) {
      console.error('Upload error:', err);
      await errorAlert(setAlrt, isRTL);
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data: any) => {
    setSaving(true);

    const variables: any = {
      _id: row?._id ? row._id : undefined,
      title: data.title,
      titleAr: data.titleAr,
      desc: data.desc,
      filename: fileInfo?.filename,
      filetype: fileInfo?.filetype,
      onlinename: fileInfo?.onlinename,
      url: fileInfo?.url,
      size: fileInfo?.size,
    };

    const mutate = isNew ? addAction : editAction;
    const mutateName = isNew ? 'createArchive' : 'updateArchive';
    await apply(mutate, mutateName, variables);
  };

  const apply = async (mutate: any, mutateName: string, variables: any) => {
    try {
      const res = await mutate({ variables });
      if (res?.data?.[mutateName]?.ok === false) {
        await errorAlert(setAlrt, isRTL);
        setSaving(false);
        return;
      }
      closeModal();
      await successAlert(setAlrt, isRTL);
    } catch (error) {
      onError(error);
    }
  };

  const onHandleSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const closeModal = () => {
    onClose();
    setSaving(false);
    setFileInfo(null);
    setUploadProgress(0);
    reset();
  };

  const onError = async (error: any) => {
    if (error.message.includes('duplicate')) {
      await dublicateAlert(setAlrt, isRTL);
    } else {
      await errorAlert(setAlrt, isRTL);
      reset();
      console.log(error);
    }
  };

  const title = isRTL
    ? isNew
      ? 'إضافة مستند'
      : 'تعديل مستند'
    : isNew
      ? 'New Document'
      : 'Edit Document';

  return (
    <PopupLayout
      isRTL={isRTL}
      open={open}
      onClose={closeModal}
      title={title}
      onSubmit={onHandleSubmit}
      theme={theme}
      alrt={alrt}
      maxWidth={'sm'}
      saving={saving}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextFieldLocal
            name="title"
            label={isRTL ? 'اسم المستند' : 'Document Name'}
            register={register}
            errors={errors}
            row={row}
            fullWidth
            mb={0}
          />
        </Grid>
        <Grid item xs={6}>
          <TextFieldLocal
            name="titleAr"
            label={isRTL ? 'اسم المستند بالعربي' : 'Document Name (Ar)'}
            register={register}
            errors={errors}
            row={row}
            fullWidth
            mb={0}
          />
        </Grid>

        <Grid item xs={12}>
          <TextFieldLocal
            name="desc"
            label={isRTL ? 'الوصف' : 'Description'}
            register={register}
            errors={errors}
            row={row}
            fullWidth
            multiline
            rows={3}
            mb={0}
          />
        </Grid>

        {/* PDF Upload */}
        <Grid item xs={12}>
          <Box
            style={{
              border: '1px dashed #ccc',
              borderRadius: 8,
              padding: 16,
              textAlign: 'center',
            }}
          >
            {fileInfo ? (
              <Box display="flex" alignItems="center" justifyContent="center">
                <PictureAsPdfIcon
                  style={{ marginRight: 8, color: '#d32f2f' }}
                />
                <Typography>{fileInfo.filename}</Typography>
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => setFileInfo(null)}
                  style={{ marginLeft: 16 }}
                >
                  {isRTL ? 'إزالة' : 'Remove'}
                </Button>
              </Box>
            ) : (
              <>
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="archive-file-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="archive-file-upload">
                  <Button
                    variant="outlined"
                    color="primary"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                  >
                    {isRTL ? 'رفع ملف PDF' : 'Upload PDF'}
                  </Button>
                </label>
              </>
            )}
            {uploading && (
              <Box mt={2}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption">
                  {isRTL ? 'جاري الرفع...' : 'Uploading...'}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </PopupLayout>
  );
};

export default PopupArchive;
